import PageHero from "@/components/PageHero";
import LocalizedLink from "@/components/LocalizedLink";
import CommunityActions from "@/components/CommunityActions";
import { Handshake, CalendarDays, MessageCircle, HeartHandshake, ArrowUpLeft, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * /<lang>/contact — Contact hub.
 *
 * Previously this was a plain “reasons to contact us” page with 4 descriptions
 * and no real action. It is now the entry point to the 4 internal request
 * forms. Each card routes to an internal form page; there are no external
 * links or mailto targets.
 */

const C = {
  ar: {
    eyebrow: "تواصل",
    title: "تواصل معنا",
    lead: "اختر نوع الطلب المناسب لاستكمال نموذج مختصر، وسيتواصل معكم فريق SAS في أقرب وقت. تجري جميع الطلبات داخل الموقع مباشرة.",
    cta: "فتح النموذج",
    communityEyebrow: "تواصل وانضم إلى المجتمع",
    communityTitle: "تواصل وانضم إلى المجتمع",
    communityLead: "إذا كنت تفضّل التواصل المباشر بدل ملء نموذج، استخدم إحدى القنوات أدناه.",
    closingEyebrow: "خاتمة",
    closing:
      "نبني معاً مساراً معرفياً عربياً عملياً في الذكاء الاصطناعي، أكثر قرباً من الناس وأكثر ملائمة لواقع سوريا.",
    items: [
      {
        slug: "partnership",
        icon: Handshake,
        title: "طلب تعاون أو شراكة",
        desc: "للجامعات والمدارس والمنظمات والمبادرات الراغبة بالتعاون مع SAS.",
      },
      {
        slug: "host-workshop",
        icon: CalendarDays,
        title: "طلب استضافة ورشة",
        desc: "لطلب ورشة تعريفية أو تطبيقية داخل جامعتكم أو مؤسستكم أو مركزكم.",
      },
      {
        slug: "general",
        icon: MessageCircle,
        title: "تواصل عام",
        desc: "للأسئلة والاستفسارات والاقتراحات وطلب المزيد من المعلومات.",
      },
      {
        slug: "volunteer",
        icon: HeartHandshake,
        title: "الانضمام كمساهم أو متطوع",
        desc: "للمساهمة في أنشطة SAS بمهاراتكم أو وقتكم أو خبراتكم.",
      },
      {
        slug: "v2-feedback",
        icon: Sparkles,
        title: "قدّم اقتراحًا لـ SAS V2",
        desc: "شاركنا اقتراحًا لأداة أو ميزة أو مقال ضمن الإصدار القادم. يمكن الإرسال بشكل مجهول.",
        badge: "V2",
      },
    ],
  },
  en: {
    eyebrow: "Contact",
    title: "Get in touch",
    lead: "Pick the request type that fits you best to complete a short internal form. The SAS team will get back to you soon. All requests are handled inside the website.",
    cta: "Open form",
    communityEyebrow: "Contact & community",
    communityTitle: "Contact & Community",
    communityLead: "Prefer to reach out directly instead of filling a form? Pick a channel below.",
    closingEyebrow: "Closing",
    closing:
      "Together we are building a practical Arabic-first knowledge path in AI — closer to people and better fit for the Syrian context.",
    items: [
      {
        slug: "partnership",
        icon: Handshake,
        title: "Partnership and collaboration request",
        desc: "For universities, schools, organisations, and initiatives interested in working with SAS.",
      },
      {
        slug: "host-workshop",
        icon: CalendarDays,
        title: "Host a workshop",
        desc: "Request an introductory or practical AI workshop inside your university, institution, or centre.",
      },
      {
        slug: "general",
        icon: MessageCircle,
        title: "General contact",
        desc: "For questions, information requests, suggestions, or general inquiries.",
      },
      {
        slug: "volunteer",
        icon: HeartHandshake,
        title: "Join as a contributor or volunteer",
        desc: "Support SAS with your skills, time, or experience.",
      },
      {
        slug: "v2-feedback",
        icon: Sparkles,
        title: "Submit a suggestion for SAS V2",
        desc: "Share a tool, feature, or article idea for the next version. Anonymous submissions are welcome.",
        badge: "V2",
      },
    ],
  },
};

export default function Contact() {
  const { lang } = useLanguage();
  const c = C[lang];

  return (
    <>
      <PageHero eyebrow={c.eyebrow} title={c.title} lead={c.lead} />
      <section className="sas-section" data-testid="contact-reasons">
        <div className="sas-container">
          <div className="sas-grid-2">
            {c.items.map(({ slug, icon: Icon, title, desc, badge }) => (
              <LocalizedLink
                key={slug}
                to={`/contact/${slug}`}
                className="sas-card flex gap-4 items-start group transition-colors hover:border-[color:var(--sas-ink)]"
                data-testid={`contact-card-${slug}`}
              >
                <div
                  className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center"
                  style={{ background: "var(--sas-accent-soft)", color: "var(--sas-accent)" }}
                  aria-hidden
                >
                  <Icon size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="sas-h3 flex items-center gap-2 flex-wrap">
                    <span>{title}</span>
                    {badge && (
                      <span
                        className="v2-badge inline-flex items-center justify-center min-w-[26px] h-[20px] px-1.5 rounded-full text-[10.5px] font-bold tracking-wide"
                        aria-hidden
                      >
                        {badge}
                      </span>
                    )}
                  </h3>
                  <p className="sas-body mt-2 text-[15px]">{desc}</p>
                  <span className="sas-btn-link mt-4 inline-flex">
                    {c.cta} <ArrowUpLeft size={14} />
                  </span>
                </div>
              </LocalizedLink>
            ))}
          </div>
        </div>
      </section>
      <div className="sas-divider" />
      <section className="sas-section" data-testid="contact-community">
        <div className="sas-container">
          <div className="max-w-3xl mb-10">
            <div className="sas-eyebrow mb-2">{c.communityEyebrow}</div>
            <h2 className="sas-h2" data-testid="contact-community-title">
              {c.communityTitle}
            </h2>
            <p className="sas-body mt-4 text-[15px]">{c.communityLead}</p>
          </div>
          <CommunityActions
            lang={lang}
            variant="block"
            testId="contact-community-actions"
          />
        </div>
      </section>
      <div className="sas-divider" />
      <section className="sas-section" data-testid="contact-closing">
        <div className="sas-container max-w-3xl text-center">
          <div className="sas-eyebrow mb-4">{c.closingEyebrow}</div>
          <p className="sas-h2">{c.closing}</p>
        </div>
      </section>
    </>
  );
}
